import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { FieldSubscription } from 'final-form';
import { isUndefined } from 'lodash';
import React from 'react';
import { Field } from 'react-final-form';

import type { SelectOption } from '@swf/components/Inputs/components/DropDown/DropDown';
import type { InjectedValidatorProps } from '@swf/core/hoc/withValidator';
import withValidator from '@swf/core/hoc/withValidator';
import usePauseValidator from '@swf/core/hooks/usePauseValidator';
import useTrackers from '@swf/core/hooks/useTrackers';
import useTrackingLabel from '@swf/core/hooks/useTrackingLabel';
import type { TrackedTranslation } from '@swf/core/types/i18n';

import styles from './RawInput.scss';
import type { Props as DropDownProps } from './components/DropDown/DropDown';
import DropDown from './components/DropDown/DropDown';
import Label from './components/Label/Label';

export interface OwnProps {
  id: string;
  label: TrackedTranslation | string;
  hideLabel?: boolean;
  values?: Record<string, string | number>;
  name?: string;
  subscription?: FieldSubscription;
  caption?: React.ReactNode;
  labelClassName?: string;
  labelContent?: React.ReactNode;
  defaultOption?: SelectOption;
  icon?: React.ReactNode;
}
export type Props = DropDownProps & InjectedValidatorProps & OwnProps;

const StarsDropDown: React.FC<Props> = ({
  id,
  name,
  caption,
  onSelect,
  hideLabel,
  validator,
  values,
  validationErrors,
  label,
  defaultOption,
  labelClassName,
  labelContent,
  icon,
  disabled,
  ...rest
}) => {
  const { inputFilled, inputFocused } = useTrackers();
  const fieldName = useTrackingLabel(label, values);
  usePauseValidator();

  return (
    <Field id={id} name={name || id} validate={validator} initialValue={defaultOption?.value}>
      {({ input, meta }) => (
        <>
          <Label
            id={id}
            hidden={hideLabel}
            className={classNames(labelClassName, {
              [styles.error]: (meta.touched || meta.dirty) && meta.error
            })}
          >
            {typeof label === 'string' ? label : <Translation {...label} values={values} />}
            {labelContent}
          </Label>
          <DropDown
            id={id}
            meta={meta}
            name={name || id}
            value={isUndefined(input.value) ? defaultOption?.value : input.value}
            onFocus={() => inputFocused({ fieldName, fieldType: 'dropdown' })}
            onSelect={(option) => {
              const value = option?.value;
              input.onChange(value);
              !disabled &&
                inputFilled({
                  fieldName,
                  strInput: isUndefined(value) ? value : value.toString(),
                  fieldType: 'dropdown',
                  isAutoFilled: false
                });
              onSelect?.(option);
            }}
            icon={icon}
            disabled={disabled}
            {...rest}
          />
          {caption && <div className={styles.captionExplanation}>{caption}</div>}
          {(meta.touched || meta.dirty) && meta.error && meta.error.message && (
            <p className={styles.error}>
              <Translation {...meta.error.message} />
            </p>
          )}
        </>
      )}
    </Field>
  );
};

export default withValidator(StarsDropDown);
